'use client';

import {
  ArrowRight,
  Check,
  Clock,
  Crown,
  Sparkles,
  Star,
  Users,
  Zap,
  ChevronDown,
  ChevronUp,
  X,
} from 'lucide-react';
import Link from 'next/link';
import React, { useEffect, useMemo, useState, useTransition, useRef } from 'react';
import { cn } from '@/lib/utils';
import { appUrl, apiUrl } from '@/lib/config';
import { trackEvent } from '@/lib/analytics/events';
import { reconcilePlan } from '@/features/billing/utils/reconcile-plans';

export type BillingInterval = 'MONTHLY' | 'YEARLY';
export type BillingPlanType = 'FREE' | 'PAID' | 'ENTERPRISE';

export interface BillingPlanFeatureValue {
  key: string;
  label: string;
  value: string | number | boolean | null;
  included: boolean;
}

export interface BillingPlanPriceView {
  id: string;
  provider: string;
  interval: BillingInterval;
  currency: string;
  amount: number;
}

export interface BillingPlanLimitView {
  metric: string;
  limit: number | null;
}

export interface BillingPlanView {
  id: string;
  name: string;
  slug: string;
  description: string;
  type: BillingPlanType;
  trialDays: number;
  isPublic: boolean;
  sortOrder: number;
  popular?: boolean;
  cta?: string | null;
  features: BillingPlanFeatureValue[];
  limits?: BillingPlanLimitView[];
  prices: BillingPlanPriceView[];
}

function formatMinorUnit(amountInCents: number, currency: string = 'USD') {
  if (amountInCents === 0) return 'Free';
  const mainAmount = amountInCents / 100;
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: mainAmount % 1 === 0 ? 0 : 2,
  }).format(mainAmount);
}

const PLAN_ORDER = ['free', 'starter', 'pro', 'enterprise'] as const;
const FEATURE_ORDER = [
  'maxProjects',
  'maxWorkspaces',
  'storageLimitMB',
  'dailyDownloads',
  'dailyTestEmails',
  'versionHistoryDays',
  'maxVersions',
  'monthlyApiLimit',
  'rateLimitPerMinute',
  'apiAccess',
  'monthlyExports',
  'monthlySpamChecks',
  'aiCredits',
  'dailyAIGenerations',
  'monthlyAIGenerations',
  'lifetimeAIGenerations',
  'byok',
  'autoSave',
  'comparison',
  'thumbnails',
  'publicSharing',
  'imageExport',
  'mjmlExport',
  'teamSupport',
  'customStorage',
  'whiteLabel',
  'removeBranding',
] as const;

const LIMIT_FEATURE_PATTERN = /max|limit|daily|monthly|history|storage|versions/i;

type PlanAction = {
  href: string;
  label: string;
  disabled?: boolean;
};

type FeatureDisplayRow =
  | {
      type: 'group';
      key: string;
      label: string;
    }
  | {
      type: 'feature';
      feature: BillingPlanFeatureValue;
      valueLabel: string | null;
    };

const FEATURE_CATEGORY_MAP: Record<string, string> = {
  maxProjects: 'Limits & Quotas',
  maxWorkspaces: 'Limits & Quotas',
  storageLimitMB: 'Limits & Quotas',
  dailyTestEmails: 'Limits & Quotas',
  monthlySpamChecks: 'Limits & Quotas',
  monthlyExports: 'Limits & Quotas',
  dailyDownloads: 'Limits & Quotas',
  versionHistoryDays: 'Limits & Quotas',
  maxVersions: 'Limits & Quotas',
  monthlyApiLimit: 'Limits & Quotas',
  rateLimitPerMinute: 'Limits & Quotas',
  apiAccess: 'Limits & Quotas',

  aiCredits: 'AI Capabilities',
  dailyAIGenerations: 'AI Capabilities',
  monthlyAIGenerations: 'AI Capabilities',
  lifetimeAIGenerations: 'AI Capabilities',
  byok: 'AI Capabilities',

  autoSave: 'Editor Features',
  comparison: 'Editor Features',
  thumbnails: 'Editor Features',
  publicSharing: 'Editor Features',
  removeBranding: 'Editor Features',

  imageExport: 'Export Options',
  mjmlExport: 'Export Options',

  teamSupport: 'Team & Support',
  customStorage: 'Team & Support',
  whiteLabel: 'Team & Support',
};

const CATEGORY_ORDER = [
  'Limits & Quotas',
  'AI Capabilities',
  'Editor Features',
  'Export Options',
  'Team & Support',
  'Other Features',
];

function normalizeSlug(slug?: string | null) {
  return slug?.trim().toLowerCase() ?? '';
}

function getPlanRank(plan: Pick<BillingPlanView, 'slug' | 'sortOrder'>) {
  const orderIndex = PLAN_ORDER.indexOf(normalizeSlug(plan.slug) as (typeof PLAN_ORDER)[number]);
  return orderIndex >= 0 ? orderIndex : plan.sortOrder;
}

function uniqueSortedPlans(plans: BillingPlanView[]) {
  const bySlug = new Map<string, BillingPlanView>();

  for (const plan of plans) {
    const slug = normalizeSlug(plan.slug);
    if (!slug || bySlug.has(slug)) continue;
    bySlug.set(slug, plan);
  }

  return Array.from(bySlug.values()).sort((a, b) => {
    const byRank = getPlanRank(a) - getPlanRank(b);
    return byRank === 0 ? a.name.localeCompare(b.name) : byRank;
  });
}

function isIncludesFeature(feature: BillingPlanFeatureValue) {
  return feature.key.startsWith('includes_');
}

function isFeatureAvailable(feature: BillingPlanFeatureValue) {
  if (!feature.included || isIncludesFeature(feature)) return false;
  if (feature.value === false) return false;
  if (typeof feature.value === 'number' && feature.value <= 0) return false;
  return true;
}

function formatFeatureValue(feature: BillingPlanFeatureValue) {
  if (!feature.included || feature.value === false) {
    if (LIMIT_FEATURE_PATTERN.test(feature.key)) {
      return '—';
    }
    return null;
  }

  if (typeof feature.value === 'number') {
    if (feature.value <= 0) {
      if (LIMIT_FEATURE_PATTERN.test(feature.key)) {
        return '—';
      }
      return null;
    }

    if (feature.key === 'aiCredits') {
      return feature.value.toLocaleString();
    }
    if (feature.key === 'maxProjects' || feature.key === 'maxWorkspaces' || feature.key === 'maxVersions') {
      return feature.value.toLocaleString();
    }
    if (feature.key === 'storageLimitMB') {
      return feature.value >= 1000
        ? `${(feature.value / 1000).toLocaleString()} GB`
        : `${feature.value} MB`;
    }
    if (feature.key === 'dailyTestEmails') {
      return `${feature.value} / day`;
    }
    if (feature.key === 'dailyDownloads') {
      return `${feature.value} / day`;
    }
    if (feature.key === 'monthlyExports') {
      return `${feature.value} / mo`;
    }
    if (feature.key === 'monthlySpamChecks') {
      return `${feature.value} / mo`;
    }
    if (feature.key === 'versionHistoryDays') {
      return feature.value === 1 ? '1 day' : `${feature.value} days`;
    }
    if (feature.key === 'monthlyApiLimit') {
      return `${feature.value.toLocaleString()} / mo`;
    }
    if (feature.key === 'rateLimitPerMinute') {
      return `${feature.value} / min`;
    }

    return feature.value.toLocaleString();
  }

  if (feature.value === true) {
    if (LIMIT_FEATURE_PATTERN.test(feature.key)) {
      return 'Unlimited';
    }
    return null;
  }

  if (typeof feature.value === 'string') {
    return feature.value;
  }

  return null;
}

function featureValueSignature(feature: BillingPlanFeatureValue) {
  return JSON.stringify({
    available: isFeatureAvailable(feature),
    value: formatFeatureValue(feature),
  });
}

function sortFeatures(features: BillingPlanFeatureValue[]) {
  return [...features].sort((a, b) => {
    const aIsGroup = isIncludesFeature(a);
    const bIsGroup = isIncludesFeature(b);
    if (aIsGroup !== bIsGroup) return aIsGroup ? -1 : 1;

    const aIndex = FEATURE_ORDER.indexOf(a.key as (typeof FEATURE_ORDER)[number]);
    const bIndex = FEATURE_ORDER.indexOf(b.key as (typeof FEATURE_ORDER)[number]);
    const aRank = aIndex >= 0 ? aIndex : FEATURE_ORDER.length;
    const bRank = bIndex >= 0 ? bIndex : FEATURE_ORDER.length;

    return aRank === bRank ? a.label.localeCompare(b.label) : aRank - bRank;
  });
}

function buildFeatureRows(
  plan: BillingPlanView,
  previousPlan?: BillingPlanView
): FeatureDisplayRow[] {
  const previousFeatures = new Map<string, BillingPlanFeatureValue>(
    (previousPlan?.features ?? [])
      .filter((feature: BillingPlanFeatureValue) => !isIncludesFeature(feature))
      .map((feature: BillingPlanFeatureValue) => [feature.key, feature])
  );
  const hasIncludesGroup = plan.features.some(
    (feature: BillingPlanFeatureValue) => isIncludesFeature(feature) && feature.included && feature.value !== false
  );
  const rows: FeatureDisplayRow[] = [];
  let renderedGroup = false;

  for (const feature of sortFeatures(plan.features)) {
    if (isIncludesFeature(feature)) {
      if (!renderedGroup && feature.included && feature.value !== false) {
        rows.push({ type: 'group', key: feature.key, label: feature.label });
        renderedGroup = true;
      }
      continue;
    }

    if (!isFeatureAvailable(feature)) continue;

    let displayLabel = feature.label;
    if (feature.key === 'aiCredits') {
      const isFree = normalizeSlug(plan.slug) === 'free';
      displayLabel = isFree ? 'AI Credits (Lifetime)' : 'AI Credits / mo';
    } else if (
      ['dailyAIGenerations', 'monthlyAIGenerations', 'lifetimeAIGenerations'].includes(feature.key)
    ) {
      const isFree = normalizeSlug(plan.slug) === 'free';
      if (isFree) {
        if (feature.key !== 'lifetimeAIGenerations') continue;
        displayLabel = 'AI Credits (Lifetime)';
      } else {
        if (feature.key !== 'monthlyAIGenerations') continue;
        displayLabel = 'AI Credits / mo';
      }
    }

    const previousFeature = previousFeatures.get(feature.key);
    const unchangedFromPrevious =
      hasIncludesGroup &&
      previousFeature &&
      featureValueSignature(previousFeature) === featureValueSignature(feature);

    if (unchangedFromPrevious) continue;

    rows.push({
      type: 'feature',
      feature: { ...feature, label: displayLabel },
      valueLabel: formatFeatureValue(feature),
    });
  }

  return rows;
}

function getPlanIcon(type: BillingPlanType, slug: string) {
  if (type === 'FREE') return <Sparkles size={22} className="text-on-surface-disabled" />;
  if (slug === 'starter') return <Zap size={22} className="text-pb-blue-500 dark:text-pb-blue-400" />;
  if (slug === 'pro') return <Crown size={22} className="text-amber-500 dark:text-amber-400" />;
  return <Users size={22} className="text-primary" />;
}

function getPlanAction({
  plan,
  priceCurrency,
  interval,
  isCustom,
}: {
  plan: BillingPlanView;
  priceCurrency: string;
  interval: BillingInterval;
  currentPlan?: string;
  isCurrent: boolean;
  isCustom: boolean;
}): PlanAction {
  if (isCustom) {
    return {
      href: '/talktosales',
      label: 'Talk to Sales',
    };
  }

  if (plan.type === 'FREE') {
    return { href: appUrl('/sign-up'), label: 'Start Building Free' };
  }

  return {
    href: appUrl(`/checkout?plan=${plan.slug}&interval=${interval}&currency=${priceCurrency}`),
    label: 'Get Started',
  };
}





export function DynamicPricingTable({
  plans,
  currency,
  provider,
  currentPlan,
  hideContactSales = false,
}: {
  plans: BillingPlanView[];
  currency: string;
  provider: string;
  currentPlan?: string;
  hideContactSales?: boolean;
}) {
  const [interval, setInterval] = useState<BillingInterval>('MONTHLY');
  const [localCurrency, setLocalCurrency] = useState((currency || 'USD').toUpperCase());
  const [activePlans, setActivePlans] = useState<BillingPlanView[]>(() => (plans || []).map(reconcilePlan));
  const [isPending, startTransition] = useTransition();
  const [showMatrix, setShowMatrix] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActivePlans((plans || []).map(reconcilePlan));
  }, [plans]);

  useEffect(() => {
    let cancelled = false;
    async function fetchDynamicPlans() {
      try {
        const query = new URLSearchParams();
        if (localCurrency) query.set('currency', localCurrency);
        const res = await fetch(apiUrl(`/api/billing/plans?${query.toString()}`));
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.plans && Array.isArray(data.plans) && data.plans.length > 0) {
          setActivePlans(data.plans.map(reconcilePlan));
        }
      } catch (err) {
        console.warn('Failed to fetch live dynamic billing plans:', err);
      }
    }
    void fetchDynamicPlans();
    return () => {
      cancelled = true;
    };
  }, [localCurrency]);



  const currencies = useMemo(() => {
    const set = new Set<string>();
    for (const plan of activePlans) {
      for (const price of plan.prices ?? []) {
        if (price.currency) {
          set.add(price.currency.toUpperCase());
        }
      }
    }
    if (!set.has('USD')) set.add('USD');
    if (!set.has('INR')) set.add('INR');
    return Array.from(set).sort();
  }, [activePlans]);

  const normalizedCurrentPlan = normalizeSlug(currentPlan);
  const visiblePlans = useMemo(() => uniqueSortedPlans(activePlans), [activePlans]);

  const dynamicMatrix = useMemo(() => {
    const categories = new Map<
      string,
      Map<string, { label: string; values: Record<string, BillingPlanFeatureValue | null> }>
    >();

    for (const plan of visiblePlans) {
      for (const feature of plan.features) {
        if (isIncludesFeature(feature)) continue;

        let displayKey = feature.key;
        let displayLabel = feature.label;

        if (feature.key === 'apiAccess') {
          displayLabel = 'Developer REST API Access & Keys';
        } else if (feature.key === 'byok') {
          displayLabel = 'AI Keys & Custom Model Providers (BYOK)';
        } else if (
          feature.key === 'aiCredits' ||
          ['dailyAIGenerations', 'monthlyAIGenerations', 'lifetimeAIGenerations'].includes(
            feature.key
          )
        ) {
          displayKey = 'aiCredits';
          displayLabel = 'AI Credits';
        }

        const category = FEATURE_CATEGORY_MAP[feature.key] || 'Other Features';

        if (!categories.has(category)) categories.set(category, new Map());
        const catMap = categories.get(category)!;

        if (!catMap.has(displayKey)) {
          catMap.set(displayKey, { label: displayLabel, values: {} });
        }

        if (displayKey === 'aiCredits') {
          const isFree = normalizeSlug(plan.slug) === 'free';
          if (feature.key === 'aiCredits') {
            catMap.get(displayKey)!.values[plan.id] = feature;
          } else if (isFree && feature.key === 'lifetimeAIGenerations') {
            catMap.get(displayKey)!.values[plan.id] = feature;
          } else if (!isFree && feature.key === 'monthlyAIGenerations') {
            catMap.get(displayKey)!.values[plan.id] = feature;
          }
        } else {
          catMap.get(displayKey)!.values[plan.id] = feature;
        }
      }
    }

    return Array.from(categories.entries())
      .sort((a, b) => CATEGORY_ORDER.indexOf(a[0]) - CATEGORY_ORDER.indexOf(b[0]))
      .map(([category, featuresMap]) => ({
        category,
        features: Array.from(featuresMap.entries())
          .sort((a, b) => {
            const aIndex = FEATURE_ORDER.indexOf(a[0] as any);
            const bIndex = FEATURE_ORDER.indexOf(b[0] as any);
            return (aIndex >= 0 ? aIndex : 999) - (bIndex >= 0 ? bIndex : 999);
          })
          .map(([, data]) => data),
      }));
  }, [visiblePlans]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const urlCurrency = params.get('currency')?.toUpperCase();
      if (urlCurrency && (urlCurrency === 'USD' || urlCurrency === 'INR')) {
        setLocalCurrency(urlCurrency);
      }
    }
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCurrencyChange = (newCurrency: string) => {
    const formatted = newCurrency.toUpperCase();
    startTransition(() => {
      setLocalCurrency(formatted);
    });
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('currency', formatted);
      window.history.replaceState({}, '', url.toString());
    }
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-6 py-2 md:py-20">
      <div className="border-outline-variant animate-fade-in-up flex flex-col items-center justify-between gap-6 border-b pb-10 md:flex-row">
        <div className="text-center md:text-left">
          <div className="bg-primary-container border-primary-container text-primary dark:text-pb-blue-400 text-label-caps inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-bold">
            <Sparkles size={12} className="animate-pulse" />
            {localCurrency === 'INR'
              ? 'Optimized for India (Razorpay)'
              : 'Global billing enabled (Paddle)'}
          </div>
          <p className="text-body-sm text-on-surface-variant dark:text-pb-slate-300 mt-2 font-medium">
            Find the perfect tier for email design productivity.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-6">
          <div className="flex flex-col items-center gap-2 md:items-start">
            <label className="text-on-surface-variant dark:text-pb-slate-300 text-[12px] font-bold tracking-wider uppercase">
              Billing Cycle
            </label>
            <div className="border-outline-variant bg-surface-container-high relative inline-flex rounded-full border p-1 shadow-inner backdrop-blur-md">
              <div
                className="bg-primary absolute inset-y-1 rounded-full shadow-md transition-all duration-300 ease-out"
                style={{
                  left: interval === 'MONTHLY' ? '0.25rem' : '50%',
                  width: 'calc(50% - 0.25rem)',
                }}
              />

              {(['MONTHLY', 'YEARLY'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setInterval(value)}
                  className={`text-body-sm relative z-10 w-[100px] rounded-full py-1.5 font-bold transition-colors duration-300 ${
                    interval === value
                      ? 'text-on-primary'
                      : 'text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  {value === 'MONTHLY' ? 'Monthly' : 'Yearly'}
                  {value === 'YEARLY' && (
                    <span className="absolute -top-2 -right-3 rounded-full border border-pb-white bg-emerald-700 px-2 py-0.5 text-[10.5px] font-black text-pb-white shadow-sm ring-1 ring-emerald-700/10">
                      -20%
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {currencies.length > 1 && (
            <div ref={dropdownRef} className="flex flex-col items-center gap-2 md:items-start">
              <label className="text-on-surface-variant dark:text-pb-slate-300 text-[12px] font-bold tracking-wider uppercase">
                Currency
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => !isPending && setDropdownOpen(!dropdownOpen)}
                  disabled={isPending}
                  className={cn(
                    'border-outline-variant bg-surface-container text-body-sm text-on-surface focus:ring-primary-container flex h-10 w-24 items-center justify-between rounded-full border px-4 font-bold transition-all outline-none focus:ring-2',
                    isPending
                      ? 'cursor-not-allowed opacity-50'
                      : 'hover:border-primary active:scale-95'
                  )}
                  aria-haspopup="listbox"
                  aria-expanded={dropdownOpen}
                  aria-label={`Currency: ${localCurrency}`}
                >
                  <span>{localCurrency}</span>
                  <ChevronDown
                    size={14}
                    className={cn(
                      'text-on-surface-variant/60 transition-transform duration-200',
                      dropdownOpen && 'rotate-180'
                    )}
                  />
                </button>

                {dropdownOpen && (
                  <div
                    className="border-outline-variant bg-surface-container-high/95 animate-in fade-in zoom-in-95 absolute right-0 z-50 mt-2 w-24 rounded-2xl border p-1 shadow-xl backdrop-blur-md duration-150"
                    role="listbox"
                  >
                    {currencies.map((item) => {
                      const isSelected = item === localCurrency;
                      return (
                        <button
                          key={item}
                          type="button"
                          onClick={() => {
                            handleCurrencyChange(item);
                            setDropdownOpen(false);
                          }}
                          className={cn(
                            'text-body-sm flex w-full cursor-pointer items-center justify-between rounded-xl px-3 py-2 text-left font-bold transition-colors duration-200',
                            isSelected
                              ? 'bg-primary text-on-primary'
                              : 'text-on-surface-variant hover:bg-surface-container-highest hover:text-on-surface'
                          )}
                          role="option"
                          aria-selected={isSelected}
                        >
                          {item}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        className={cn(
          'mx-auto mt-16 grid max-w-6xl items-stretch gap-8 transition-opacity duration-300 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3',
          isPending && 'pointer-events-none opacity-50'
        )}
      >
        {visiblePlans.map((plan, planIndex) => {
          const slug = normalizeSlug(plan.slug);
          const previousPlan = planIndex > 0 ? visiblePlans[planIndex - 1] : undefined;
          const targetCurrency = localCurrency.toUpperCase();

          let exactPrice = plan.prices?.find(
            (item: BillingPlanPriceView) =>
              item.interval?.toUpperCase() === interval &&
              item.currency?.toUpperCase() === targetCurrency &&
              (provider ? item.provider?.toUpperCase() === provider.toUpperCase() : true)
          );

          if (!exactPrice) {
            exactPrice = plan.prices?.find(
              (item: BillingPlanPriceView) =>
                item.interval?.toUpperCase() === interval &&
                item.currency?.toUpperCase() === targetCurrency
            );
          }

          let priceCurrency = targetCurrency;
          let priceAmount: number | undefined;

          if (exactPrice && typeof exactPrice.amount === 'number') {
            priceAmount = exactPrice.amount;
            priceCurrency = exactPrice.currency.toUpperCase();
          } else {
            const usdPrice =
              plan.prices?.find(
                (item: BillingPlanPriceView) =>
                  item.interval?.toUpperCase() === interval &&
                  (item.currency?.toUpperCase() === 'USD' || !item.currency)
              ) ??
              plan.prices?.find(
                (item: BillingPlanPriceView) => item.interval?.toUpperCase() === interval
              ) ??
              plan.prices?.[0];

            if (usdPrice && typeof usdPrice.amount === 'number') {
              if (targetCurrency === 'INR') {
                priceAmount = Math.round((usdPrice.amount / 100) * 85) * 100;
                priceCurrency = 'INR';
              } else {
                priceAmount = usdPrice.amount;
                priceCurrency = (usdPrice.currency || 'USD').toUpperCase();
              }
            } else if (usdPrice) {
              priceAmount = usdPrice.amount;
              priceCurrency = (usdPrice.currency || 'USD').toUpperCase();
            }
          }
          const isCurrent = normalizedCurrentPlan === slug;
          const isFeatured = slug === 'pro';
          const hasFixedPrice = typeof priceAmount === 'number';
          const isFree = plan.type === 'FREE' || priceAmount === 0;
          const isCustom = plan.type === 'ENTERPRISE' || !hasFixedPrice;

          let annualSavingsPercent = 0;
          if (!isFree && !isCustom && interval === 'YEARLY' && priceCurrency) {
            const monthlyPriceObj = plan.prices?.find(
              (item: BillingPlanPriceView) =>
                item.interval?.toUpperCase() === 'MONTHLY' &&
                item.currency?.toUpperCase() === priceCurrency
            );
            const yearlyPriceObj = plan.prices?.find(
              (item: BillingPlanPriceView) =>
                item.interval?.toUpperCase() === 'YEARLY' &&
                item.currency?.toUpperCase() === priceCurrency
            );
            if (monthlyPriceObj && yearlyPriceObj && monthlyPriceObj.amount > 0) {
              const annualCostIfMonthly = monthlyPriceObj.amount * 12;
              const actualYearlyCost = yearlyPriceObj.amount;
              if (annualCostIfMonthly > actualYearlyCost) {
                annualSavingsPercent = Math.round(
                  ((annualCostIfMonthly - actualYearlyCost) / annualCostIfMonthly) * 100
                );
              }
            }
          }

          const action = getPlanAction({
            plan,
            priceCurrency,
            interval,
            currentPlan: normalizedCurrentPlan,
            isCurrent,
            isCustom,
          });
          const featureRows = buildFeatureRows(plan, previousPlan);

          return (
            <article
              key={plan.id}
              className={cn(
                'bg-surface-container-lowest animate-fade-in-up group relative flex h-full flex-col rounded-[2rem] border p-8 transition-all duration-300',
                isFeatured
                  ? 'border-primary ring-primary-container z-10 scale-100 shadow-[0_4px_30px_var(--primary-glow)] ring-2 hover:-translate-y-2 hover:shadow-[0_20px_50px_-10px_rgba(21,93,252,0.35)]'
                  : 'border-outline hover:-translate-y-1.5 hover:border-outline/80 hover:shadow-xl'
              )}
              style={{ animationDelay: `${planIndex * 80}ms` }}
            >
              {isFeatured && (
                <div className="from-primary/[0.04] pointer-events-none absolute inset-0 rounded-[2rem] bg-gradient-to-b to-transparent" />
              )}

              {isFeatured && (
                <div className="bg-primary text-label-caps text-on-primary shadow-primary/30 absolute -top-4.5 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full px-4.5 py-1.5 font-bold tracking-wider whitespace-nowrap shadow-lg">
                  <Star size={11} className="fill-current" />
                  Most Popular
                </div>
              )}

              <div className="mb-6 flex items-center gap-4">
                <div
                  className={cn(
                    'border-outline flex h-12 w-12 items-center justify-center rounded-xl border shadow-sm transition-transform duration-300 group-hover:scale-110',
                    isFeatured
                      ? 'bg-primary-container text-primary border-primary-container'
                      : 'bg-surface-container-low text-on-surface'
                  )}
                >
                  {getPlanIcon(plan.type, slug)}
                </div>
                <div>
                  <h3 className="text-headline-md text-on-surface leading-tight font-bold">
                    {plan.name}
                  </h3>
                  {isCurrent && (
                    <span className="inline-flex items-center gap-0.5 rounded bg-emerald-500/10 px-2 py-0.5 text-[11px] font-bold tracking-wider text-emerald-500 uppercase">
                      <Check size={11} strokeWidth={3} /> Active
                    </span>
                  )}
                </div>
              </div>

              <p className="text-body-sm text-on-surface-variant dark:text-pb-slate-300 mb-6 min-h-10 leading-relaxed font-medium">
                {plan.description}
              </p>

              <div className="bg-surface-container-low border-outline relative mb-6 rounded-2xl border p-5 shadow-sm">
                {plan.trialDays > 0 && (
                  <div className="absolute -top-3.5 left-5 inline-flex items-center gap-1 rounded-full border border-emerald-500/20 bg-emerald-500/15 px-3 py-0.5 text-[11.5px] font-bold tracking-wider text-emerald-700 dark:text-emerald-400 uppercase shadow-sm">
                    <Clock size={11} strokeWidth={2.5} /> {plan.trialDays}-day free trial
                  </div>
                )}
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="text-on-surface text-4xl font-extrabold tracking-tight">
                    {typeof priceAmount === 'number'
                      ? formatMinorUnit(priceAmount, priceCurrency)
                      : 'Custom'}
                  </span>
                  {hasFixedPrice && !isFree && (
                    <span className="text-body-sm text-on-surface-variant dark:text-pb-slate-300 font-semibold">
                      /{interval === 'MONTHLY' ? 'mo' : 'yr'}
                    </span>
                  )}
                </div>
                {!isFree && interval === 'YEARLY' && annualSavingsPercent > 0 && (
                  <p className="mt-1 text-[12px] font-bold text-emerald-500">
                    Save {annualSavingsPercent}% on annual billing
                  </p>
                )}
                <p className="text-on-surface-variant dark:text-pb-slate-300 mt-2 text-[12px] font-bold tracking-wider uppercase">
                  {plan.type === 'FREE' ? 'Single workspace access' : 'Workspace subscription'}
                </p>
              </div>

              <Link
                href={action.href}
                prefetch={false}
                onClick={() => {
                  trackEvent('pricing_cta_click', {
                    plan_id: plan.id || slug,
                    plan_name: plan.name,
                    billing_cycle: interval?.toLowerCase() as 'monthly' | 'yearly',
                    amount: priceAmount,
                    currency: priceCurrency,
                    cta_name: action.label,
                    cta_location: 'pricing_table_card',
                    destination: action.href,
                  });
                }}
                className={cn(
                  'group/btn mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-xl px-5 text-xs font-bold tracking-wider uppercase transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0',
                  isFeatured
                    ? 'bg-primary text-on-primary shadow-primary/20 hover:shadow-primary/30 shadow-lg hover:shadow-xl'
                    : 'border-outline-variant bg-surface text-on-surface hover:bg-surface-container hover:border-outline border hover:shadow-md'
                )}
              >
                {action.label}
                <ArrowRight size={14} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
              </Link>

              <div className="mt-8 flex flex-1 flex-col justify-between">
                <div className="space-y-5">
                  <p className="text-on-surface-variant dark:text-pb-slate-300 text-[12px] font-bold tracking-wider uppercase">
                    Key Features Included:
                  </p>
                  <div className="space-y-3.5">
                    {featureRows.map((row) => {
                      if (row.type === 'group') {
                        return (
                          <div key={row.key} className="flex items-center gap-2 pt-3 pb-1">
                            <div className="bg-pb-slate-200 dark:bg-pb-white/10 h-px flex-1" />
                            <span className="text-pb-blue-600 dark:text-pb-blue-400 text-center text-[11px] font-bold tracking-wider uppercase flex items-center gap-1.5 px-1">
                              <Sparkles size={11} /> {row.label}
                            </span>
                            <div className="bg-pb-slate-200 dark:bg-pb-white/10 h-px flex-1" />
                          </div>
                        );
                      }

                      return (
                        <div key={row.feature.key} className="flex items-center justify-between gap-3 text-body-sm">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div
                              className={cn(
                                'flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full',
                                isFeatured
                                  ? 'bg-pb-blue-500/20 text-pb-blue-600 dark:text-pb-blue-400'
                                  : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                              )}
                            >
                              <Check size={10} strokeWidth={3.5} />
                            </div>
                            <span className="text-on-surface-variant dark:text-pb-slate-200 leading-tight font-medium truncate">
                              {row.feature.label}
                            </span>
                          </div>
                          {row.valueLabel && (
                            <span
                              className={cn(
                                'shrink-0 rounded-full px-2 py-0.5 font-mono text-[11px] font-bold tracking-tight',
                                row.valueLabel === 'Unlimited'
                                  ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                                  : 'bg-pb-blue-500/10 dark:bg-pb-blue-500/20 text-pb-blue-700 dark:text-pb-blue-300'
                              )}
                            >
                              {row.valueLabel}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <div className="mx-auto mt-24 flex max-w-4xl flex-col items-center">
        <button
          onClick={() => setShowMatrix(!showMatrix)}
          className="border-outline-variant hover:bg-surface-container bg-surface-container-low text-on-surface-variant hover:text-on-surface flex items-center gap-2 rounded-full border px-6 py-3 text-xs font-bold tracking-wider uppercase shadow-sm transition-all"
        >
          {showMatrix ? 'Hide detailed feature comparison' : 'Compare all plans & features'}
          {showMatrix ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {showMatrix && (
          <div className="border-outline bg-surface-container-lowest animate-in fade-in mt-10 w-full overflow-hidden rounded-2xl border shadow-xl backdrop-blur-md duration-300">
            <div className="overflow-x-auto p-4 md:p-8">
              <table className="w-full min-w-[600px] border-collapse text-left">
                <thead>
                  <tr className="border-outline-variant border-b">
                    <th className="text-on-surface-variant dark:text-pb-slate-300 w-[25%] min-w-[150px] pr-4 pb-5 text-xs font-bold tracking-widest uppercase">
                      Capability
                    </th>
                    {visiblePlans.map((plan) => {
                      const isFeatured = normalizeSlug(plan.slug) === 'pro';
                      return (
                        <th
                          key={plan.id}
                          className={cn(
                            'min-w-[120px] px-4 pb-5 text-center text-xs font-bold tracking-widest uppercase',
                            isFeatured
                              ? 'text-primary bg-primary/[0.03] rounded-t-xl pr-6 pl-4'
                              : 'text-on-surface-variant dark:text-pb-slate-300'
                          )}
                        >
                          {plan.name}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {dynamicMatrix.map((group) => (
                    <React.Fragment key={group.category}>
                      <tr className="bg-surface-container-low">
                        <td
                          colSpan={visiblePlans.length + 1}
                          className="text-on-surface-variant/75 px-4 py-3.5 text-[12px] font-bold tracking-wider uppercase"
                        >
                          {group.category}
                        </td>
                      </tr>

                      {group.features.map((featureData) => (
                        <tr
                          key={featureData.label}
                          className="border-outline-variant hover:bg-surface-container-low border-b transition-colors"
                        >
                          <td className="text-body-sm text-on-surface/90 py-4.5 pr-4 pl-4 font-bold">
                            {featureData.label}
                          </td>
                          {visiblePlans.map((plan) => {
                            const f = featureData.values[plan.id];
                            const isFeatured = normalizeSlug(plan.slug) === 'pro';
                            const available = f && isFeatureAvailable(f);
                            const formattedValue = f ? formatFeatureValue(f) : null;

                            return (
                              <td
                                key={plan.id}
                                className={cn(
                                  'text-body-sm px-4 py-4.5 text-center font-semibold',
                                  isFeatured
                                    ? 'text-primary bg-primary/[0.03] pr-6 pl-4'
                                    : 'text-on-surface-variant/75'
                                )}
                              >
                                {formattedValue !== null ? (
                                  formattedValue === '—' ? (
                                    <span className="text-on-surface-variant/40">—</span>
                                  ) : (
                                    formattedValue
                                  )
                                ) : available ? (
                                  <Check
                                    className={cn(
                                      'mx-auto',
                                      isFeatured
                                        ? 'text-primary drop-shadow-sm'
                                        : 'text-emerald-500'
                                    )}
                                    size={16}
                                    strokeWidth={3}
                                  />
                                ) : (
                                  <X
                                    className="text-on-surface-variant/30 mx-auto"
                                    size={14}
                                    strokeWidth={2.5}
                                  />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {!hideContactSales && (
        <div className="relative overflow-hidden rounded-3xl border border-pb-blue-500/30 bg-gradient-to-br from-[#0c1830] via-[#091224] to-[#060c18] p-8 md:p-10 shadow-2xl mx-auto mt-20 max-w-4xl text-center">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-96 bg-pb-blue-500/15 rounded-full blur-3xl pointer-events-none" />
          <div className="relative z-10 flex flex-col items-center gap-4">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3.5 py-1 text-[11px] font-bold uppercase tracking-wider text-amber-400 shadow-sm backdrop-blur-sm">
              <Star size={12} className="fill-amber-400" />
              <span>Enterprise &amp; Agency Workspaces</span>
            </div>
            <h4 className="text-2xl sm:text-3xl font-extrabold text-pb-white tracking-tight">
              Need a custom workspace or dedicated SLA?
            </h4>
            <p className="max-w-xl text-sm sm:text-base text-pb-slate-300 leading-relaxed font-medium">
              We provide tailored templates, custom asset catalogs, dedicated staging infrastructure, white labeling, and custom SLAs for agencies and high-volume publishers.
            </p>
            <Link
              href="/talktosales"
              className="group mt-2 inline-flex h-11 items-center gap-2 rounded-xl bg-pb-blue-600 px-6 text-sm font-bold text-pb-white shadow-lg shadow-pb-blue-600/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-pb-blue-500 hover:shadow-xl hover:shadow-pb-blue-600/50 active:translate-y-0"
            >
              <span>Contact Sales Team</span>
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
